import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { Dialog as D } from "radix-ui";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";
import { t } from "@/i18n/id";

export const Dialog = D.Root;
export const DialogTrigger = D.Trigger;
export const DialogClose = D.Close;

export const overlayClass = "fixed inset-0 z-50 bg-slate-950/40 backdrop-blur-[2px] data-[state=open]:animate-fade-in";

export interface DialogContentProps extends Omit<ComponentPropsWithoutRef<typeof D.Content>, "title"> {
  title: ReactNode;
  description?: ReactNode;
  hideTitle?: boolean;
  size?: "sm" | "md" | "lg" | "xl" | "wide" | "workspace";
  hideClose?: boolean;
  footer?: ReactNode;
  /** When false, the body does not scroll. The caller lays out its own scroll regions. */
  scroll?: boolean;
}

const widths = {
  sm: "sm:max-w-md",
  md: "sm:max-w-lg",
  lg: "sm:max-w-2xl",
  xl: "sm:max-w-4xl",
  wide: "sm:max-w-6xl",
  workspace: "h-[92dvh] sm:h-[min(900px,92dvh)] sm:max-w-[min(1360px,calc(100%-2rem))]",
};

export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(function DialogContent(
  { title, description, hideTitle, size = "md", hideClose, footer, scroll = true, className, children, ...props },
  ref,
) {
  return (
    <D.Portal>
      <D.Overlay className={overlayClass} />
      <D.Content
        ref={ref}
        className={cn(
          "fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] flex-col rounded-t-[28px] bg-white shadow-2xl outline-none data-[state=open]:animate-sheet-up",
          "sm:inset-auto sm:left-1/2 sm:top-1/2 sm:w-[calc(100%-2rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-card sm:data-[state=open]:animate-pop",
          widths[size],
          className,
        )}
        {...(description ? {} : { "aria-describedby": undefined })}
        {...props}
      >
        <div className="flex items-start gap-3 px-5 pt-5 sm:px-6 sm:pt-6">
          <div className={cn("min-w-0 flex-1", hideTitle && "sr-only")}>
            <D.Title className="text-lg font-bold tracking-tight text-slate-900">{title}</D.Title>
            {description ? <D.Description className="mt-1 text-sm leading-relaxed text-slate-500">{description}</D.Description> : null}
          </div>
          {!hideClose ? (
            <D.Close className="-mr-2 -mt-1 ml-auto grid size-10 shrink-0 place-items-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label={t.common.close}>
              <X className="size-5" aria-hidden="true" />
            </D.Close>
          ) : null}
        </div>
        <div className={cn("min-h-0 flex-1", scroll ? "scrollbar-thin overflow-y-auto px-5 pb-5 pt-4 sm:px-6 sm:pb-6" : "flex flex-col overflow-hidden")}>{children}</div>
        {footer ? <div className="safe-bottom flex flex-col-reverse gap-2 border-t border-line px-5 py-4 sm:flex-row sm:justify-end sm:px-6">{footer}</div> : null}
      </D.Content>
    </D.Portal>
  );
});
